import React, { FunctionComponent, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';

import {
  ConfirmationMessageModal,
  MenuItem,
  ScrollToTop,
  TableActionsMenu,
} from '@Components/index';
import StateTsrTable from '@Components/StateTsrTable';
import CommonConstants from '@Constants/common.constants';
import StateTsrModal from '@Containers/Settings/AdminSettings/StateTsrModal';
import {
  getStateTsrRows,
  getStateTsrRTKQuery,
} from '@Containers/Settings/Settings.utils';
import { StateTsrQuery } from '@Models/settings.models';
import {
  useDeleteStateTsrMutation,
  useGetStateTsrListQuery,
} from '@Services/settings.service';
import {
  BaseAlertProps,
  ConfirmationActionTypes,
  DialerCrudTypes,
} from '@Typings/global.types';
import query from '@Utils/common/query.utils';
import useAlert from '@Utils/hooks/useAlert.hook';

const StateTsr: FunctionComponent = (): JSX.Element => {
  const { t } = useTranslation();
  const { triggerAlert } = useAlert();
  const location = useLocation();

  /** QUERIES */
  const stateTsrQuery: StateTsrQuery = useMemo(
    () => getStateTsrRTKQuery(location.search),
    [location.search]
  );

  const [stateTsrId, setStateTsrId] = useState<string>('');
  const [stateName, setStateName] = useState<string>('');
  const [isConfirmationModalOpen, setIsConfirmationModalOpen] = useState(false);
  const [confirmationActionType, setConfirmationActionType] =
    useState<ConfirmationActionTypes | null>(null);
  const [modalCrudType, setModalCrudType] = useState<DialerCrudTypes | null>(
    null
  );

  /** RTK QUERY HOOK */
  const {
    data,
    isLoading,
    isFetching,
    error: errorGetStateTsr,
  } = useGetStateTsrListQuery(stateTsrQuery);

  const [deleteStateTsr, deleteStateTsrMethods] = useDeleteStateTsrMutation();

  const [alertState, setAlertState] = useState<BaseAlertProps>({
    openAlert: false,
  });

  /** Table Loading State */
  const loading = isLoading || isFetching || deleteStateTsrMethods.isLoading;

  useEffect(() => {
    // DELETE SUCCESS
    if (deleteStateTsrMethods.isSuccess) {
      triggerAlert(
        CommonConstants.GLOBAL_MESSAGES.STATE_TSR_DELETE,
        setAlertState
      );
    }

    // DELETE ERROR
    if (deleteStateTsrMethods.error) {
      triggerAlert(deleteStateTsrMethods.error, setAlertState);
    }
  }, [
    deleteStateTsrMethods.isSuccess,
    deleteStateTsrMethods.error,
    triggerAlert,
  ]);

  /** Handle Error and Success Alert  -->
   */
  useEffect(() => {
    setStateTsrId('');
    setStateName('');

    // QUERY ERROR
    if (errorGetStateTsr) {
      triggerAlert(errorGetStateTsr, setAlertState);
    }
  }, [errorGetStateTsr, triggerAlert]);

  /** STATE TSR ACTION HANDLERS --> */
  const handleStateTsrAdd = () => {
    setModalCrudType(DialerCrudTypes.Add);
    setStateTsrId('');
    setStateName('');
  };

  /** HANDLES STATE TSR DELETE */
  const handleDelete = (id: string) => deleteStateTsr(id);

  /** Handles confirmation click of modal and performs action accordingly */
  const handleConfirmationModalClick = () => {
    setIsConfirmationModalOpen(false);
    if (confirmationActionType === ConfirmationActionTypes.Delete) {
      return handleDelete(stateTsrId);
    }
  };

  /** Table Actions Component Function
   *
   * Returns the table actions component with required menu items.
   */
  const actionsComponent = (id: string, stateFullName: string): JSX.Element => {
    const handleTableActionsClick = (
      confirmationType: ConfirmationActionTypes
    ) => {
      setIsConfirmationModalOpen(true);
      setConfirmationActionType(confirmationType);
      setStateTsrId(id);
      setStateName(stateFullName);
    };

    /** Actions Menu Data for non Deleted Rows */
    const actionsMenu: MenuItem[] = [
      {
        label: t('common.messages.Edit'),
        icon: 'edit',
        value: id,
        onClick: () => {
          setModalCrudType(DialerCrudTypes.Edit);
          setStateTsrId(id);
        },
      },
      {
        label: t('common.messages.Delete'),
        icon: 'delete',
        value: id,
        onClick: () => {
          setStateTsrId(id);
          handleTableActionsClick(ConfirmationActionTypes.Delete);
        },
      },
    ];

    return <TableActionsMenu menuItems={actionsMenu} />;
  };

  /** ROWS AND TOTAL_ROWS_COUNT */
  const rows = getStateTsrRows(data?.table, actionsComponent);
  const totalRowsCount = data?.table[0]?.fullCount
    ? parseInt(data?.table[0].fullCount, 10)
    : CommonConstants.INITIAL_ROWS_PER_PAGE;

  return (
    <>
      <ScrollToTop
        dependencies={[
          query.get(CommonConstants.QUERY_PARAMS.COMMON.PAGE_NO) || '',
          query.get(CommonConstants.QUERY_PARAMS.COMMON.PAGE_SIZE) || '',
        ]}
      />
      <StateTsrModal
        stateTsrId={stateTsrId}
        crudType={modalCrudType}
        setCrudType={setModalCrudType}
        deleteBtnOnClick={() => handleDelete(stateTsrId)}
      />
      <ConfirmationMessageModal
        headerText={t('common.messages.Warning')}
        isModalOpen={isConfirmationModalOpen}
        setIsModalOpen={setIsConfirmationModalOpen}
        message={t('adminSettings.Delete TSR Message', {
          interpolation: {
            escapeValue: false,
          },
          state: stateName,
        })}
        confirmButtonText={t('common.messages.Remove')}
        cancelButtonVariant="outlined"
        onClick={handleConfirmationModalClick}
      />
      <StateTsrTable
        alertState={alertState}
        tabChild
        stateTsrAddBtnClick={() => handleStateTsrAdd()}
        tableProps={{
          loading,
          rows,
          paginationProps: {
            count: totalRowsCount,
          },
        }}
      />
    </>
  );
};

export default StateTsr;
